/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useCallback, useEffect } from "react";
import { InputError, isEmptyError } from "~/tools";
import { useDatadogRUM } from "./useDatadogRum";

type ErrorContext = Record<string, unknown>;

function useReportError(): {
  reportError: (error: unknown, context?: ErrorContext) => void;
};

function useReportError(error?: InputError): void;

function useReportError(
  inputError?: InputError | undefined,
  context?: ErrorContext
) {
  const ddRum = useDatadogRUM();

  const reportError = useCallback(
    (error: unknown, errorContext?: ErrorContext) => {
      ddRum?.addError(error, errorContext);
    },
    [ddRum]
  );

  useEffect(() => {
    if (isEmptyError(inputError)) {
      return;
    }

    reportError(inputError, context);

    // We're using inputError.name here instead of inputError, because inputError caused infinite rerenders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputError?.name, reportError]);

  if (typeof inputError === "undefined") {
    return { reportError };
  }
}

export { useReportError };
